import { useMemo, useCallback } from "react"
import { filter } from "lodash"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createFactorSensitivityMultiDateQuery, createFactorSensitivityIntradayBatchTrendQueries } from "gatherers/multi-date/factor-sensitivity"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { graphFactorSensitivityTemplateKey, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { FactorSensitivityModelMetadata as ModelMetadata, type SensitivityType } from "./types"
import { DEFAULT_THRESHOLD_PARAMS } from "helpers/threshold"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { useSourceGraphMetricIds, getEnabledItemIds } from "../../hooks/use-source-graph-metric-ids"
import { useGraphAddSeries } from "../../hooks/use-graph-add-series"
import { buildCustomGraphAvailableMetrics } from "../custom/helpers"
import { getResolvableAddedMetrics } from "../custom/family"

interface FactorSensitivityGraphProps {
  modelMetadata: ModelMetadata
  sensitivityType: SensitivityType
  onMetricsChange?: SourceMetricIdsChangeHandler
}

interface FactorSensitivityItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: string
}

function createFactorSensitivityItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
  addedMetrics: string[] = [],
): FactorSensitivityItem[] {
  if (!modelMetadata?.factors) return []

  const categoryFactors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)
  const categoryFactorIds = new Set(categoryFactors.map(factor => factor.id))
  const addedFactors = addedMetrics.length
    ? filter(modelMetadata.factors, factor => addedMetrics.includes(factor.id) && !categoryFactorIds.has(factor.id))
    : []

  return [...categoryFactors, ...addedFactors].map(factor => ({
    id: factor.id,
    label: factor.label,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
  }))
}

export default function FactorSensitivityGraph({ modelMetadata, sensitivityType, onMetricsChange }: FactorSensitivityGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
    addedMetrics,
    addMetric,
    seriesColorSlots,
    setSeriesColorSlots,
    selectedTemplate,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(graphFactorSensitivityTemplateKey(sensitivityType), GRAPH_CATEGORY_DEFAULTS)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const drilldownView = sensitivityType === "historical" ? "historicalFactorSensitivity" : "predictedFactorSensitivity"

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined, legendClickedDate?: string, legendClickedBatchId?: string) => {
    if (!panelId || !focusedItemId) return
    // A batch-trend point is stamped with its own batch's asOf, not the latest — resolve the
    // clicked point back to its batch; a legend click has no clickPosition, so legendClickedBatchId stands in.
    const clickedBatch = isBatchTrendMode
      ? completedBatches.find(batch => batch.id === (clickPosition?.batchId ?? legendClickedBatchId))
      : undefined
    // clickedBatch.date is authoritative; clickPosition.date can shift a day across timezones.
    // legendClickedDate (the legend-value path, no clickPosition) falls in behind both.
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? legendClickedDate ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.factor-sensitivity",
      title: "Asset View: Factor Sensitivity",
      view: drilldownView,
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A live-tick click has no clickedBatch; don't fall back to intradayBatchMetadata's latest batch.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: focusedItemId,
        selectedCategory,
        clickedDate: snapshotDate,
      },
    }))
  }, [panelId, openDrilldown, drilldownView, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  // Same intent as a chart-dot click, seeded with the series' own last date — the legend has no click coordinates.
  const handleLegendValueClick = useCallback((focusedItemId: string, legendClickedDate?: string, legendClickedBatchId?: string) => {
    handleDotClick(undefined, focusedItemId, legendClickedDate, legendClickedBatchId)
  }, [handleDotClick])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter, normalization)
  const segment = useFilteredSegment(portfolioFilter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(
    () => createFactorSensitivityItems(modelMetadata, selectedCategory, disabledItems, addedMetrics),
    [modelMetadata, selectedCategory, disabledItems, addedMetrics],
  )

  const enabledFactorIds = useMemo(
    () => getEnabledItemIds(currentItems),
    [currentItems],
  )
  useSourceGraphMetricIds({ itemIds: enabledFactorIds, onMetricsChange })

  const availableMetricsGroups = useMemo(() => buildCustomGraphAvailableMetrics(modelMetadata), [modelMetadata])
  const categoryFactorIds = useMemo(
    () => createFactorSensitivityItems(modelMetadata, selectedCategory, disabledItems).filter(item => !item.disabled).map(item => item.id),
    [modelMetadata, selectedCategory, disabledItems],
  )
  // Excludes added-from-other-category factors so an id hidden elsewhere isn't mistaken for a built-in here.
  const builtInFactorIds = useMemo(
    () => createFactorSensitivityItems(modelMetadata, selectedCategory, disabledItems).map(item => item.id),
    [modelMetadata, selectedCategory, disabledItems],
  )
  const { addSeriesMetrics, alreadyShownMetricIds, onAddSeries, trackedSetDisabledItems, trackedSetOnlyItem, filterItems, seriesSelection } = useGraphAddSeries({
    availableMetricsGroups,
    visibleItemIds: categoryFactorIds,
    builtInItemIds: builtInFactorIds,
    addedMetrics,
    addMetric,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    seriesColors: chartColors.series,
    seriesColorSlots,
    setSeriesColorSlots,
  })

  // Filters out a stale id, or noDataAddedCount keeps its "picked but has no data" notice up forever.
  const resolvableAddedMetrics = useMemo(
    () => getResolvableAddedMetrics(addedMetrics, selectedTemplate, modelMetadata, null),
    [addedMetrics, selectedTemplate, modelMetadata],
  )

  const gatherers = useMemo(() => {
    const liveQuery = createFactorSensitivityMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      sensitivityType,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    )
    // Batches append to the live-tick series instead of replacing it.
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...createFactorSensitivityIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches, sensitivityType)]
  }, [isBatchTrendMode, completedBatches, modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, sensitivityType, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"
  const noDataMessage = isOneDayMode
    ? "Intraday factor sensitivity data isn't available yet."
    : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: trackedSetDisabledItems,
      setOnlyItem: trackedSetOnlyItem,
      enableItems,
      thresholdParams: DEFAULT_THRESHOLD_PARAMS,
      onDotClick: handleDotClick,
      onLegendValueClick: handleLegendValueClick,
      loadingMessage: "Loading factor sensitivity data...",
      errorMessage: "Failed to load factor sensitivity data. Please try again.",
      emptyMessage: "No factor sensitivity data available for the selected period.",
      noDataMessage,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
      legendPlacement: "right",
      addSeriesMetrics,
      alreadyShownMetricIds,
      onAddSeries,
      seriesSelection,
      addedMetricIds: resolvableAddedMetrics,
    }}
  />
}
